import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Sun, Moon, User, Building, Mail, Phone, Plus, Trash2, Edit2, Save, X,
  Users, Loader2, Shield, Lock, Eye, EyeOff, Edit, FileSpreadsheet, Download,
  FileText, Star, Copy, Info, CreditCard, Calendar, CheckCircle
} from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';

const API = RUNTIME_BACKEND_URL;

const placeholders = [
  { key: '{bride_name}', description: 'Gelin adi' },
  { key: '{groom_name}', description: 'Damat adi' },
  { key: '{wedding_date}', description: 'Dugun tarihi' },
  { key: '{services}', description: 'Secilen hizmetler' },
  { key: '{total_price}', description: 'Toplam tutar' },
  { key: '{deposit_amount}', description: 'Kapora tutari' },
  { key: '{remaining_amount}', description: 'Kalan tutar' },
];

export default function SalonSettingsPage() {
  const { user, updateTheme, updateUser, token } = useAuthStore();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newVenue, setNewVenue] = useState({ name: '', capacity: '' });
  const [editVenue, setEditVenue] = useState({ name: '', capacity: '' });
  const [isExporting, setIsExporting] = useState(false);

  // Plan info
  const [planInfo, setPlanInfo] = useState(null);

  // User templates
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', content: '', is_default: false });
  
  // Profile & Password
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    company_name: user?.company_name || '',
    phone: user?.phone || ''
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    loadVenues();
    fetchPlanInfo();
    fetchTemplates();
  }, []);

  const fetchPlanInfo = async () => {
    try {
      const res = await fetch(`${API}/api/my-plan`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setPlanInfo(await res.json());
    } catch (e) { /* ignore */ }
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await fetch(`${API}/api/my-templates`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setTemplates(await res.json());
    } catch (e) { /* ignore */ }
    finally { setLoadingTemplates(false); }
  };

  const loadVenues = async () => {
    try {
      const res = await fetch(`${API}/api/salon/venues`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setVenues(data);
      }
    } catch (error) {
      console.error('Venues load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVenue = async () => {
    if (!newVenue.name || !newVenue.capacity) {
      toast.error('Salon adı ve kapasite zorunludur');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/salon/venues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newVenue.name,
          capacity: parseInt(newVenue.capacity)
        })
      });

      if (!res.ok) throw new Error('Eklenemedi');

      toast.success('Salon eklendi');
      setNewVenue({ name: '', capacity: '' });
      loadVenues();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateVenue = async (venueId) => {
    if (!editVenue.name || !editVenue.capacity) {
      toast.error('Salon adı ve kapasite zorunludur');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/salon/venues/${venueId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: editVenue.name,
          capacity: parseInt(editVenue.capacity)
        })
      });

      if (!res.ok) throw new Error('Güncellenemedi');

      toast.success('Salon güncellendi');
      setEditingId(null);
      loadVenues();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVenue = async (venueId, venueName) => {
    if (!confirm(`"${venueName}" salonunu silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      const res = await fetch(`${API}/api/salon/venues/${venueId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Silinemedi');
      }

      toast.success('Salon silindi');
      loadVenues();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const startEditing = (venue) => {
    setEditingId(venue.id);
    setEditVenue({ name: venue.name, capacity: venue.capacity.toString() });
  };

  const handleThemeToggle = () => {
    const newTheme = user?.theme === 'dark' ? 'light' : 'dark';
    updateTheme(newTheme);
  };

  const openProfileDialog = () => {
    setProfileForm({
      full_name: user?.full_name || '',
      company_name: user?.company_name || '',
      phone: user?.phone || ''
    });
    setShowProfileDialog(true);
  };

  const handleSaveProfile = async () => {
    if (!profileForm.full_name.trim()) {
      toast.error('Ad Soyad zorunludur');
      return;
    }

    setSavingProfile(true);
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileForm)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Güncellenemedi');
      }

      const data = await res.json();
      updateUser(data.user);
      toast.success('Profil güncellendi');
      setShowProfileDialog(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password) {
      toast.error('Mevcut şifrenizi girin');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalı');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }

    setSavingPassword(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Şifre değiştirilemedi');
      }

      toast.success('Şifre başarıyla değiştirildi');
      setShowPasswordDialog(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingPassword(false);
    }
  };

  // --- Template CRUD ---
  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', content: '', is_default: templates.length === 0 });
    setShowTemplateDialog(true);
  };
  const openEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({ name: t.name, content: t.content, is_default: t.is_default });
    setShowTemplateDialog(true);
  };
  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) { toast.error('Sablon adi zorunlu'); return; }
    if (!templateForm.content.trim()) { toast.error('Sablon icerigi zorunlu'); return; }
    setSavingTemplate(true);
    try {
      const url = editingTemplate ? `${API}/api/my-templates/${editingTemplate.id}` : `${API}/api/my-templates`;
      const method = editingTemplate ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(templateForm) });
      if (!res.ok) throw new Error('Kaydedilemedi');
      toast.success(editingTemplate ? 'Sablon guncellendi' : 'Sablon olusturuldu');
      setShowTemplateDialog(false); fetchTemplates();
    } catch (e) { toast.error(e.message); }
    finally { setSavingTemplate(false); }
  };
  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Bu sablonu silmek istediginize emin misiniz?')) return;
    try {
      const res = await fetch(`${API}/api/my-templates/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Silinemedi');
      toast.success('Sablon silindi'); fetchTemplates();
    } catch (e) { toast.error(e.message); }
  };
  const handleSetDefault = async (t) => {
    try {
      await fetch(`${API}/api/my-templates/${t.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ is_default: true }) });
      toast.success(`"${t.name}" varsayilan yapildi`); fetchTemplates();
    } catch (e) { toast.error('Guncellenemedi'); }
  };
  const copyTemplateContent = (content) => { navigator.clipboard.writeText(content); toast.success('Kopyalandi'); };

  const getPlanPrice = () => {
    if (planInfo?.user_price && planInfo.user_price > 0) return planInfo.user_price;
    if (planInfo?.plan) {
      if (planInfo.subscription_plan === 'monthly') return planInfo.plan.price_monthly;
      if (planInfo.subscription_plan === 'yearly') return planInfo.plan.price_yearly;
    }
    return 0;
  };
  const getPlanPriceLabel = () => {
    const price = getPlanPrice();
    if (!price) return '-';
    if (planInfo?.subscription_plan === 'yearly') return `${price.toLocaleString('tr-TR')} TL/yil`;
    return `${price.toLocaleString('tr-TR')} TL/ay`;
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="salon-settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Ayarlar</h1>
        <p className="text-muted-foreground mt-1">Hesap ve salon ayarlarınız</p>
      </div>

      {/* Profile Info */}
      <Card className="animate-fade-in">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Profil Bilgileri
          </CardTitle>
          <Button variant="outline" size="sm" onClick={openProfileDialog}>
            <Edit className="h-4 w-4 mr-1" /> Düzenle
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Ad Soyad</Label>
              <p className="font-medium mt-1">{user?.full_name}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">E-posta</Label>
              <p className="font-medium mt-1 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {user?.email}
              </p>
            </div>
            {user?.phone && (
              <div>
                <Label className="text-muted-foreground">Telefon</Label>
                <p className="font-medium mt-1 flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {user.phone}
                </p>
              </div>
            )}
            {user?.company_name && (
              <div>
                <Label className="text-muted-foreground">İşletme Adı</Label>
                <p className="font-medium mt-1 flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  {user.company_name}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Abonelik Bilgileri */}
      <Card className="animate-fade-in" data-testid="salon-subscription-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            Abonelik Bilgileri
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <Label className="text-muted-foreground text-xs">Durum</Label>
              <p className="font-medium mt-1 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                {user?.subscription_status === 'active' ? 'Aktif' : user?.subscription_status === 'pending' ? 'Onay Bekliyor' : 'Suresi Dolmus'}
              </p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <Label className="text-muted-foreground text-xs">Paket</Label>
              <p className="font-medium mt-1">{planInfo?.plan?.name || (user?.subscription_plan === 'monthly' ? 'Aylik' : user?.subscription_plan === 'yearly' ? 'Yillik' : '-')}</p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <Label className="text-muted-foreground text-xs">Baslangic</Label>
              <p className="font-medium mt-1 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                {user?.subscription_started_at ? formatDate(user.subscription_started_at.split('T')[0]) : '-'}
              </p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg">
              <Label className="text-muted-foreground text-xs">Ucret</Label>
              <p className="font-medium mt-1 text-primary text-lg" data-testid="salon-plan-price">{getPlanPriceLabel()}</p>
            </div>
          </div>
          {user?.subscription_expires_at && (
            <div className="text-sm text-muted-foreground">
              Bitis Tarihi: <strong>{formatDate(user.subscription_expires_at.split('T')[0])}</strong>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Anlasma Sablonlari */}
      <Card className="animate-fade-in" data-testid="salon-agreement-templates-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Anlasma Sablonlari
          </CardTitle>
          <Button size="sm" onClick={openNewTemplate} data-testid="salon-add-template-btn">
            <Plus className="h-4 w-4 mr-1" /> Yeni Sablon
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Musterilerinize gondereceyiniz anlasma mesajlarinin sablonlarini buradan yonetin.
          </p>
          {loadingTemplates ? (
            <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : templates.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground text-sm">
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p>Henuz sablon olusturmadiniz.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {templates.map(t => (
                <div key={t.id} className={`border rounded-lg p-3 ${t.is_default ? 'border-primary bg-primary/5' : ''}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{t.name}</span>
                      {t.is_default && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium flex items-center gap-0.5">
                          <Star className="h-2.5 w-2.5" /> Varsayilan
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => copyTemplateContent(t.content)} title="Kopyala"><Copy className="h-3.5 w-3.5" /></Button>
                      {!t.is_default && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleSetDefault(t)} title="Varsayilan Yap"><Star className="h-3.5 w-3.5" /></Button>}
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditTemplate(t)} title="Duzenle"><Edit className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteTemplate(t.id)} title="Sil"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 whitespace-pre-line">{t.content}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Salonlarım */}
      <Card className="animate-fade-in">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5 text-primary" />
            Salonlarım
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            İşletmenize ait salonları yönetin. Rezervasyon oluştururken bu salonlar arasından seçim yapılır.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Yeni Salon Ekleme */}
          <div className="flex flex-col sm:flex-row gap-3 p-4 bg-muted/50 rounded-lg">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1">Salon Adı</Label>
              <Input
                placeholder="Örn: Grand Salon"
                value={newVenue.name}
                onChange={(e) => setNewVenue({ ...newVenue, name: e.target.value })}
              />
            </div>
            <div className="w-full sm:w-32">
              <Label className="text-xs text-muted-foreground mb-1">Kapasite</Label>
              <Input
                type="number"
                placeholder="Kişi"
                value={newVenue.capacity}
                onChange={(e) => setNewVenue({ ...newVenue, capacity: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleAddVenue} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Ekle
              </Button>
            </div>
          </div>

          {/* Salon Listesi */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : venues.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Henüz salon eklenmemiş.</p>
              <p className="text-sm">Yukarıdaki formu kullanarak ilk salonunuzu ekleyin.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {venues.map((venue) => (
                <div
                  key={venue.id}
                  className="flex items-center justify-between p-3 border rounded-lg bg-card"
                >
                  {editingId === venue.id ? (
                    <>
                      <div className="flex-1 flex gap-2">
                        <Input
                          value={editVenue.name}
                          onChange={(e) => setEditVenue({ ...editVenue, name: e.target.value })}
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          value={editVenue.capacity}
                          onChange={(e) => setEditVenue({ ...editVenue, capacity: e.target.value })}
                          className="w-24"
                        />
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button size="icon" variant="ghost" onClick={() => handleUpdateVenue(venue.id)} disabled={saving}>
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 text-green-600" />}
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setEditingId(null)}>
                          <X className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{venue.name}</p>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {venue.capacity} Kişilik
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => startEditing(venue)}>
                          <Edit2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDeleteVenue(venue.id, venue.name)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security */}
      <Card className="animate-fade-in">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Güvenlik
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => setShowPasswordDialog(true)}>
            <Lock className="h-4 w-4 mr-2" />
            Şifre Değiştir
          </Button>
        </CardContent>
      </Card>

      {/* Theme */}
      <Card className="animate-fade-in">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {user?.theme === 'dark' ? (
              <Moon className="h-5 w-5 text-primary" />
            ) : (
              <Sun className="h-5 w-5 text-primary" />
            )}
            Tema Ayarları
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Koyu Tema</p>
              <p className="text-sm text-muted-foreground">
                Karanlık görünüm tercih edin
              </p>
            </div>
            <Switch
              checked={user?.theme === 'dark'}
              onCheckedChange={handleThemeToggle}
            />
          </div>
        </CardContent>
      </Card>

      {/* Export / Yedekleme */}
      <Card className="animate-fade-in">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            Veri Yedekleme
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Tüm müşteri kayıtlarınızı, rezervasyonlarınızı ve anlaşma bilgilerinizi Excel formatında indirin.
            Sistem bakıma girdiğinde bu yedek dosyasıyla verilerinize erişebilirsiniz.
          </p>
          <Button 
            variant="outline" 
            onClick={async () => {
              setIsExporting(true);
              try {
                const response = await fetch(`${API}/api/export/customers/excel`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error('Yedekleme başarısız');
                
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `rezervasyon_yedek_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('Yedekleme dosyası indirildi');
              } catch (error) {
                toast.error(error.message);
              } finally {
                setIsExporting(false);
              }
            }}
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Excel Olarak İndir
          </Button>
        </CardContent>
      </Card>

      {/* Profil Düzenleme Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Profil Düzenle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Ad Soyad *</Label>
              <Input
                value={profileForm.full_name}
                onChange={(e) => setProfileForm({...profileForm, full_name: e.target.value})}
                placeholder="Ad Soyad"
              />
            </div>
            <div className="space-y-2">
              <Label>İşletme / Salon Adı</Label>
              <Input
                value={profileForm.company_name}
                onChange={(e) => setProfileForm({...profileForm, company_name: e.target.value})}
                placeholder="İşletme adı"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input
                value={profileForm.phone}
                onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
                placeholder="05XX XXX XX XX"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowProfileDialog(false)}>
                İptal
              </Button>
              <Button onClick={handleSaveProfile} disabled={savingProfile}>
                {savingProfile && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Şifre Değiştirme Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Şifre Değiştir
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Mevcut Şifre *</Label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? "text" : "password"}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                  placeholder="Mevcut şifreniz"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Yeni Şifre *</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                  placeholder="En az 6 karakter"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Yeni Şifre (Tekrar) *</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                placeholder="Yeni şifreyi tekrar girin"
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                İptal
              </Button>
              <Button onClick={handleChangePassword} disabled={savingPassword}>
                {savingPassword && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Şifreyi Değiştir
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {editingTemplate ? 'Sablonu Duzenle' : 'Yeni Anlasma Sablonu'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Sablon Adi *</Label>
              <Input value={templateForm.name} onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})} placeholder="Ornek: Standart Anlasma" />
            </div>
            <div className="space-y-2">
              <Label>Sablon Icerigi *</Label>
              <Textarea value={templateForm.content} onChange={(e) => setTemplateForm({...templateForm, content: e.target.value})} placeholder="Anlasma metnini buraya yazin..." rows={10} className="font-mono text-sm" />
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs font-medium mb-2 flex items-center gap-1"><Info className="h-3 w-3" /> Kullanilabilir degiskenler:</p>
              <div className="flex flex-wrap gap-1.5">
                {placeholders.map(p => (
                  <button key={p.key} className="text-[11px] px-2 py-0.5 rounded bg-background border hover:bg-primary/10 transition-colors"
                    onClick={() => setTemplateForm({...templateForm, content: templateForm.content + p.key})} title={p.description}>
                    {p.key}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={templateForm.is_default} onCheckedChange={(v) => setTemplateForm({...templateForm, is_default: v})} />
              <Label className="text-sm">Varsayilan sablon olarak ayarla</Label>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Iptal</Button>
              <Button onClick={handleSaveTemplate} disabled={savingTemplate}>
                {savingTemplate && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingTemplate ? 'Guncelle' : 'Olustur'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
